import type React from 'react';
import { FormattedMessage } from 'react-intl';

import { ApplyButtonStateProvider } from '@xing-com/crate-jobs-components-apply-button';
import { AriaExtendedText } from '@xing-com/crate-jobs-components-aria-extended-text';
import { BannerContextProvider } from '@xing-com/crate-jobs-components-banner';
import { SGapCol, XsGapCol } from '@xing-com/crate-jobs-components-columns';
import {
  JobTeaserFacts,
  useAriaLabel,
} from '@xing-com/crate-jobs-components-job-teaser-options';
import { MultiLocationDisplay } from '@xing-com/crate-jobs-components-multi-location-display';
import type {
  JobTeaserData,
  JobTeaserWithHighlightsAndSummaryFragment,
} from '@xing-com/crate-jobs-graphql';
import {
  getApplicationData,
  selectJobData,
} from '@xing-com/crate-jobs-helpers';
import { useJobTeaserClickExternalTracking } from '@xing-com/crate-jobs-hooks';
import type { JobTeaserListItemProps } from '@xing-com/crate-jobs-xinglets';
import { IconApply, IconStarFilled } from '@xing-com/icons';

import { Highlight } from './highlight';
import * as S from './job-teaser-list-item.styles';
import { Summary } from './summary';

type MarkerProps = {
  isUrgentJob?: boolean;
  redirectsToThirdPartyUrl?: boolean;
  isSuperMatch: boolean;
  isLastChangeHighlight: boolean;
  matchingHighlight?: NonNullable<
    JobTeaserListItemProps['matchingHighlights']
  >['highlight'];
};

const getMarker = ({
  isUrgentJob,
  redirectsToThirdPartyUrl,
  isSuperMatch,
  isLastChangeHighlight,
  matchingHighlight,
}: MarkerProps): React.ReactNode => {
  if (isUrgentJob) {
    return (
      <S.Marker variant="strong">
        <FormattedMessage id="JOBS_HIGHLIGHT_URGENTLY_NEEDED" />
      </S.Marker>
    );
  }

  if (redirectsToThirdPartyUrl) {
    return (
      <S.Marker variant="default">
        <AriaExtendedText
          item={{ id: 'JOBS_JOB_TEASER_EXTERNAL_JOB_HIGHLIGHT' }}
          a11yItem={{
            id: 'JOBS_JOB_TEASER_EXTERNAL_JOB_HIGHLIGHT_A11Y',
          }}
        />
      </S.Marker>
    );
  }

  if (isSuperMatch && !isLastChangeHighlight) {
    return (
      <S.Marker variant="strong" icon={IconStarFilled}>
        <FormattedMessage id="JOBS_JOB_DETAILS_SKILLS_SUPER_MATCH_HEADLINE" />
      </S.Marker>
    );
  }

  if (matchingHighlight) {
    return (
      <S.Marker variant="highlight">
        {matchingHighlight.localization.localizationValue}
      </S.Marker>
    );
  }

  return null;
};

const jobIsApplicationReady = (
  job: JobTeaserData
): job is JobTeaserWithHighlightsAndSummaryFragment => {
  return (
    job.__typename === 'VisibleJob' &&
    'prioritized' in job &&
    'userInteractions' in job
  );
};

export const JobTeaserListItem: React.FC<JobTeaserListItemProps> = ({
  job,
  isNew,
  url,
  matchingHighlights,
  className,
  onClick: clickCallback,
  onContextMenu,
  onAuxClick,
  actions,
  'data-testid': dataTestId,
  headingLevel = 'h3',
  showEmploymentType,
  showAISummary,
  showApplyButton,
  'aria-label': ariaLabel,
  highlight,
  isUrgentJob,
  applyTrackingProps,
}) => {
  const {
    id,
    refreshedAt,
    title,
    locations,
    companyName,
    companyLogo,
    redirectsToThirdPartyUrl,
    applyUrl,
    summary,
  } = selectJobData(job);

  const ariaLabelText = useAriaLabel({
    ariaLabel: ariaLabel ?? title,
    redirectsToThirdPartyUrl,
    target: '_blank',
  });
  const onClick = useJobTeaserClickExternalTracking({
    isExternalJob: redirectsToThirdPartyUrl,
    jobId: id,
    url,
    clickCallback,
  });

  const matchingHighlight = matchingHighlights?.highlight;
  const isLastChangeHighlight =
    matchingHighlight?.type === 'job_highlight_last_chance';
  const isSuperMatch =
    job.__typename === 'VisibleJob' &&
    Boolean(
      job.userProfileMatches?.skills?.matchedSkills &&
      job.userProfileMatches.skills.matchedSkills.length > 3 &&
      job.paid
    );
  const Marker = getMarker({
    isUrgentJob,
    redirectsToThirdPartyUrl,
    isSuperMatch,
    isLastChangeHighlight,
    matchingHighlight,
  });

  const { slug } = job.__typename === 'VisibleJob' ? job : {};
  const application = jobIsApplicationReady(job)
    ? getApplicationData(job)
    : undefined;

  const Apply =
    showApplyButton && application?.type && slug ? (
      <BannerContextProvider>
        <ApplyButtonStateProvider
          jobSlug={slug}
          jobId={id}
          applicationType={application.type}
        >
          <S.ApplyButton
            size="small"
            application={application}
            icon={IconApply}
            trackingProps={applyTrackingProps}
          />
        </ApplyButtonStateProvider>
      </BannerContextProvider>
    ) : null;

  return (
    <S.Card
      forwardedAs="article"
      href={redirectsToThirdPartyUrl && applyUrl ? applyUrl : url}
      linkProps={{
        'aria-label': ariaLabel,
        target: '_blank',
        onClick,
        onContextMenu,
        onAuxClick,
        rel: redirectsToThirdPartyUrl
          ? 'noopener nofollow sponsored'
          : undefined,
      }}
      data-testid={dataTestId}
      className={className}
      aria-label={ariaLabelText}
      $urgentlyNeeded={isUrgentJob}
    >
      <S.Image
        type="company"
        profileName={companyName}
        size="small"
        sizeConfined="medium"
        src={companyLogo ?? ''}
        alt=""
        aria-hidden
      />
      <S.Body>
        <S.Row>
          <S.Content>
            <XsGapCol>
              {Marker}
              <S.MainInfo>
                <S.Title
                  forwardedAs={headingLevel}
                  data-testid="job-teaser-list-title"
                  noMargin
                >
                  {title}
                </S.Title>
                <S.Company noMargin size="small">
                  {companyName}
                </S.Company>
                <MultiLocationDisplay locations={locations} size="small" />
                {showAISummary && summary ? (
                  <Summary content={summary} />
                ) : null}
              </S.MainInfo>
            </XsGapCol>
            <SGapCol>
              <JobTeaserFacts
                job={job}
                matchingHighlights={matchingHighlights}
                showEmploymentType={showEmploymentType}
              />
              <Highlight highlight={highlight} />
              {refreshedAt ? (
                <S.PublicationDate date={refreshedAt} isNew={isNew} />
              ) : null}
            </SGapCol>
          </S.Content>
          <S.BigScreenCTA>{Apply}</S.BigScreenCTA>
          {actions}
        </S.Row>
        <S.SmallScreenCTA>{Apply}</S.SmallScreenCTA>
      </S.Body>
    </S.Card>
  );
};
